import React from 'react';
import { RiPlaneLine, RiTimeLine, RiCalendarLine, RiArrowRightLine, RiFlightTakeoffLine } from '@remixicon/react';

/**
 * FlightLeg Component
 * Displays a single flight leg with origin, destination, duration, and date
 *
 * @param {Object} props
 * @param {string} props.origin - Origin airport code (e.g., "TEB")
 * @param {string} props.destination - Destination airport code (e.g., "MIA")
 * @param {string} props.duration - Flight duration (e.g., "2h 52m")
 * @param {string} props.date - Flight date (e.g., "24 Oct" or ISO date)
 * @param {boolean} props.isReturn - Whether this is a return leg
 * @param {boolean} props.isLast - Whether this is the last flight leg
 */
const FlightLeg = ({ origin, destination, duration, date, isReturn = false, isLast = false }) => {
  // Format date if it's in ISO format
  const formatDate = (dateString) => {
    if (!dateString) return '';

    try {
      const dateObj = new Date(dateString);
      if (isNaN(dateObj.getTime())) return dateString; // Return as-is if invalid

      const day = dateObj.getDate();
      const month = dateObj.toLocaleString('en-US', { month: 'short' });
      return `${day} ${month}`;
    } catch {
      return dateString;
    }
  };

  const formattedDate = formatDate(date);

  return (
    <div className={`flex items-center gap-3 text-sm ${!isLast ? 'pb-2 mb-2 border-b border-gray-200' : ''}`}>
      {/* Origin to Destination */}
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1.5 px-2 py-1 bg-gray-200 rounded-2xl">
          <RiFlightTakeoffLine className="w-3.5 h-3.5 text-gray-500" />
          <span className="font-semibold text-gray-900 text-sm">{origin}</span>
        </div>
        <RiArrowRightLine className="w-3 h-3 text-gray-400" />
        <div className="flex items-center gap-1.5 px-2 py-1 bg-gray-200 rounded-2xl">
          <RiFlightTakeoffLine className="w-3.5 h-3.5 text-gray-500" />
          <span className="font-semibold text-gray-900 text-sm">{destination}</span>
        </div>
      </div>

      {/* Duration */}
      <div className="flex items-center gap-1 text-gray-500">
        <RiTimeLine className="w-3.5 h-3.5" />
        <span className="text-xs">{duration}</span>
      </div>

      {/* Date */}
      <div className="flex items-center gap-1 text-gray-500">
        <RiCalendarLine className="w-3.5 h-3.5" />
        <span className="text-xs">{formattedDate}</span>
      </div>
    </div>
  );
};

export default FlightLeg;
